import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  operationsAlertDeliveryStateSchema,
  operationsAlertEventTypeSchema,
  type OperationsAlert,
  type OperationsAlertListQuery,
} from '@erp/shared';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useOperationsAlertHistory } from '../api/use-operations-alerts';
import { formatDateTime } from '@/lib/formatters';

const PAGE_SIZE = 25;
const column = createColumnHelper<OperationsAlert>();
function deliveryVariant(state: OperationsAlert['deliveryState']): BadgeVariant {
  if (state === 'SENT') return 'success';
  if (state === 'FAILED' || state === 'UNCERTAIN') return 'danger';
  if (state === 'LIMITED') return 'warning';
  return 'secondary';
}
export function OperationsAlertHistory(): JSX.Element {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const [event, setEvent] = useState<OperationsAlertListQuery['event']>();
  const [deliveryState, setDeliveryState] = useState<OperationsAlertListQuery['deliveryState']>();
  const [selected, setSelected] = useState<OperationsAlert | null>(null);
  const history = useOperationsAlertHistory({
    page,
    pageSize: PAGE_SIZE,
    ...(event ? { event } : {}),
    ...(deliveryState ? { deliveryState } : {}),
  });
  const columns = useMemo(
    () => [
      column.accessor('occurredAt', {
        header: t('operationsAlerts.history.occurredAt'),
        cell: ({ getValue }) => formatDateTime(getValue(), i18n.language),
      }),
      column.accessor('event', {
        header: t('operationsAlerts.history.event'),
        cell: ({ getValue }) => t(`operationsAlerts.events.${getValue()}`),
      }),
      column.accessor('environment', { header: t('operationsAlerts.history.environment') }),
      column.accessor('deliveryState', {
        header: t('operationsAlerts.history.delivery'),
        cell: ({ row, getValue }) => (
          <div className="space-y-1">
            <Badge variant={deliveryVariant(getValue())}>
              {t(`operationsAlerts.deliveryStates.${getValue()}`)}
            </Badge>
            {row.original.limitReason && (
              <p className="text-xs text-muted-foreground">
                {t(`operationsAlerts.limitReasons.${row.original.limitReason}`)}
              </p>
            )}
          </div>
        ),
      }),
      column.accessor('sentAt', {
        header: t('operationsAlerts.history.sentAt'),
        cell: ({ getValue }) =>
          getValue()
            ? formatDateTime(getValue(), i18n.language)
            : t('operationsAlerts.history.notSent'),
      }),
      column.display({
        id: 'details',
        header: t('operationsAlerts.history.details'),
        cell: ({ row }) => (
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            aria-label={t('operationsAlerts.history.viewDetails')}
            onClick={() => setSelected(row.original)}
          >
            {t('operationsAlerts.history.details')}
          </Button>
        ),
      }),
    ],
    [t, i18n.language],
  );
  const table = useReactTable({
    data: history.data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });
  const clearFilters = (): void => {
    setEvent(undefined);
    setDeliveryState(undefined);
    setPage(1);
  };
  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle>{t('operationsAlerts.history.title')}</CardTitle>
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              disabled={history.isFetching}
              onClick={() => void history.refetch()}
            >
              {t('operationsAlerts.history.refresh')}
            </Button>
          </div>
          <CardDescription>{t('operationsAlerts.history.retained')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label htmlFor="alerts-event-filter" className="text-sm font-medium">
                {t('operationsAlerts.history.eventFilter')}
              </label>
              <select
                id="alerts-event-filter"
                className="flex min-h-11 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={event ?? ''}
                onChange={(change) => {
                  const value = operationsAlertEventTypeSchema.safeParse(change.target.value);
                  setEvent(value.success ? value.data : undefined);
                  setPage(1);
                }}
              >
                <option value="">{t('operationsAlerts.history.allEvents')}</option>
                {operationsAlertEventTypeSchema.options.map((value) => (
                  <option key={value} value={value}>
                    {t(`operationsAlerts.events.${value}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="alerts-delivery-filter" className="text-sm font-medium">
                {t('operationsAlerts.history.deliveryFilter')}
              </label>
              <select
                id="alerts-delivery-filter"
                className="flex min-h-11 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={deliveryState ?? ''}
                onChange={(change) => {
                  const value = operationsAlertDeliveryStateSchema.safeParse(change.target.value);
                  setDeliveryState(value.success ? value.data : undefined);
                  setPage(1);
                }}
              >
                <option value="">{t('operationsAlerts.history.allDeliveries')}</option>
                {operationsAlertDeliveryStateSchema.options.map((value) => (
                  <option key={value} value={value}>
                    {t(`operationsAlerts.deliveryStates.${value}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {history.isPending ? (
            <div
              role="status"
              aria-label={t('operationsAlerts.history.loading')}
              className="space-y-3"
            >
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : history.isError ? (
            <div role="alert" className="space-y-3 rounded-md border border-destructive/50 p-4">
              <p>{t('operationsAlerts.history.error')}</p>
              <Button
                type="button"
                variant="outline"
                className="min-h-11"
                onClick={() => void history.refetch()}
              >
                {t('common.retry')}
              </Button>
            </div>
          ) : history.data?.data.length === 0 ? (
            <div className="rounded-md border border-border p-6 text-center">
              <p>
                {t(
                  event || deliveryState
                    ? 'operationsAlerts.history.noMatches'
                    : 'operationsAlerts.history.empty',
                )}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                {t('operationsAlerts.history.emptyHint')}
              </p>
              {event || deliveryState ? (
                <Button
                  type="button"
                  variant="outline"
                  className="mt-4 min-h-11"
                  onClick={clearFilters}
                >
                  {t('operationsAlerts.history.clearFilters')}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="mt-4 min-h-11"
                  onClick={() => void history.refetch()}
                >
                  {t('operationsAlerts.history.refresh')}
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <table className="block w-full text-sm md:table">
                <caption className="sr-only">{t('operationsAlerts.history.title')}</caption>
                <thead className="hidden bg-muted/40 md:table-header-group">
                  {table.getHeaderGroups().map((group) => (
                    <tr key={group.id}>
                      {group.headers.map((header) => (
                        <th
                          key={header.id}
                          scope="col"
                          className="sticky top-0 bg-muted p-3 text-start font-medium"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="grid gap-3 md:table-row-group">
                  {table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.original.id}
                      className="grid gap-2 rounded-md border border-border p-3 md:table-row md:border-x-0 md:border-t-0 md:p-0"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="flex items-center justify-between gap-3 md:table-cell md:p-3"
                        >
                          <span
                            className="text-xs text-muted-foreground md:hidden"
                            aria-hidden="true"
                          >
                            {typeof cell.column.columnDef.header === 'string'
                              ? cell.column.columnDef.header
                              : ''}
                          </span>
                          <span>{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <nav
                aria-label={t('operationsAlerts.history.pagination')}
                className="flex flex-wrap items-center justify-between gap-3"
              >
                <p className="text-sm text-muted-foreground" role="status">
                  {t('operationsAlerts.history.page', {
                    page: history.data?.meta.page ?? page,
                    pages: Math.max(1, history.data?.meta.totalPages ?? 1),
                    total: history.data?.meta.total ?? 0,
                  })}
                </p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="min-h-11"
                    aria-label={t('operationsAlerts.history.previous')}
                    disabled={history.isFetching || page <= 1}
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                  >
                    {t('operationsAlerts.history.previous')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="min-h-11"
                    aria-label={t('operationsAlerts.history.next')}
                    disabled={history.isFetching || page >= (history.data?.meta.totalPages ?? 1)}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    {t('operationsAlerts.history.next')}
                  </Button>
                </div>
              </nav>
            </div>
          )}
        </CardContent>
      </Card>
      <Sheet
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <SheetContent
          className="[&>button]:min-h-11 [&>button]:min-w-11"
          closeLabel={t('common.close')}
        >
          <SheetHeader>
            <SheetTitle>{t('operationsAlerts.history.details')}</SheetTitle>
            <SheetDescription>{t('operationsAlerts.history.snapshotHint')}</SheetDescription>
          </SheetHeader>
          {selected && (
            <div className="space-y-5">
              <dl className="grid gap-3 sm:grid-cols-2">
                {[
                  ['occurredAt', formatDateTime(selected.occurredAt, i18n.language)],
                  ['event', t(`operationsAlerts.events.${selected.event}`)],
                  ['environment', selected.environment],
                  ['delivery', t(`operationsAlerts.deliveryStates.${selected.deliveryState}`)],
                  [
                    'sentAt',
                    selected.sentAt
                      ? formatDateTime(selected.sentAt, i18n.language)
                      : t('operationsAlerts.history.notSent'),
                  ],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-sm text-muted-foreground">
                      {t(`operationsAlerts.history.${label}`)}
                    </dt>
                    <dd className="font-medium">{value}</dd>
                  </div>
                ))}
              </dl>
              {selected.limitReason && (
                <p className="rounded-md border border-border p-3 text-sm">
                  {t(`operationsAlerts.limitReasons.${selected.limitReason}`)}
                </p>
              )}
              <div>
                <h3 className="mb-3 font-medium">{t('operationsAlerts.history.components')}</h3>
                <dl className="space-y-3">
                  {Object.entries(selected.snapshot.checks).map(([name, status]) => (
                    <div key={name} className="flex items-center justify-between gap-3">
                      <dt>
                        {t(`operationsAlerts.checks.${name}`, {
                          defaultValue: t('operationsAlerts.checks.other'),
                        })}
                      </dt>
                      <dd>
                        <Badge
                          variant={
                            status === 'down'
                              ? 'danger'
                              : status === 'warning'
                                ? 'warning'
                                : status === 'up'
                                  ? 'success'
                                  : 'secondary'
                          }
                        >
                          {t(`operationsAlerts.checkStatuses.${status}`)}
                        </Badge>
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
              <dl className="grid gap-3 sm:grid-cols-2">
                {[
                  ['queueBacklog', selected.snapshot.queueBacklog],
                  ['failedJobs', selected.snapshot.failedJobs],
                  [
                    'demoDataDetected',
                    t(
                      selected.snapshot.demoDataDetected
                        ? 'operationsAlerts.history.yes'
                        : 'operationsAlerts.history.no',
                    ),
                  ],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-sm text-muted-foreground">
                      {t(`operationsAlerts.history.${label}`)}
                    </dt>
                    <dd className="font-medium">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
